import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Download, Upload, Globe, AlertTriangle, Loader2, X, Search, FolderOpen, ChevronRight, Check, XCircle, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RestoreModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const fileRef = useRef(null);

  const handleRestore = async () => {
    if (!file) return;
    const token = localStorage.getItem("wiki_token");
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/wiki/restore?token=${token}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const d = resp.data;
      toast.success(`Restored: ${d.restored?.wiki_pages || 0} pages, ${d.restored?.wiki_templates || 0} templates, ${d.restored?.wiki_images || 0} images`);
      onClose();
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded shadow-lg max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="restore-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#a2a9b1] bg-[#f8f9fa]">
          <h3 className="font-bold text-[#202122]">Restore Wiki from Backup</h3>
          <button onClick={onClose} className="text-[#72777d] hover:text-[#202122]" data-testid="restore-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-[#fef6e7] border border-[#fc3] rounded text-sm">
            <AlertTriangle className="w-4 h-4 text-[#ac6600] flex-shrink-0 mt-0.5" />
            <p className="text-[#202122]">
              Restoring will <strong>replace all existing wiki data</strong> (pages, templates, notes, images, history) with the backup contents. This cannot be undone.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#202122] mb-1">Select backup file (.json)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".json"
              onChange={e => { setFile(e.target.files?.[0] || null); setConfirmed(false); }}
              className="text-sm w-full"
              data-testid="restore-file-input"
            />
          </div>
          {file && (
            <div className="text-sm text-[#54595d]">
              File: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
          {file && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                data-testid="restore-confirm-checkbox"
              />
              <span className="text-[#202122]">I understand this will overwrite all wiki data</span>
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[#a2a9b1] bg-[#f8f9fa]">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-[#a2a9b1] rounded hover:bg-[#eaecf0]" data-testid="restore-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleRestore}
            disabled={!file || !confirmed || restoring}
            className="px-3 py-1.5 text-sm bg-[#d33] text-white rounded hover:bg-[#b32424] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="restore-submit-btn"
          >
            {restoring ? <><Loader2 className="w-3 h-3 animate-spin" /> Restoring...</> : "Restore"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose }) {
  const [tab, setTab] = useState("search"); // "search" | "category"
  const [query, setQuery] = useState("");
  const [namespace, setNamespace] = useState("article");
  const [results, setResults] = useState([]);
  const [categoryMembers, setCategoryMembers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState({});
  const [imported, setImported] = useState({});
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const token = localStorage.getItem("wiki_token");

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const resp = await axios.get(`${API}/wiki/import/search`, {
        params: { q: query.trim(), ns: namespace, token }
      });
      setResults(resp.data.results || []);
      if (resp.data.results?.length === 0) toast.info("No results found");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleCategorySearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setCategoryMembers([]);
    setBulkResults(null);
    try {
      const resp = await axios.get(`${API}/wiki/import/category-members`, {
        params: { category: query.trim(), ns: namespace, token }
      });
      setCategoryMembers(resp.data.members || []);
      if (resp.data.members?.length === 0) toast.info("No members found in this category");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Category lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const importSingle = async (title) => {
    setImporting(prev => ({ ...prev, [title]: true }));
    try {
      const resp = await axios.post(`${API}/wiki/import/page?token=${token}`, {
        title, namespace
      });
      setImported(prev => ({ ...prev, [title]: resp.data.action }));
      toast.success(`${resp.data.action === "created" ? "Imported" : "Updated"}: ${title}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to import ${title}`);
    } finally {
      setImporting(prev => ({ ...prev, [title]: false }));
    }
  };

  const importAllCategory = async () => {
    if (!query.trim()) return;
    setBulkImporting(true);
    setBulkResults(null);
    try {
      const resp = await axios.post(`${API}/wiki/import/bulk?token=${token}`, {
        category: query.trim(), namespace
      });
      setBulkResults(resp.data);
      const s = resp.data.summary;
      toast.success(`Imported ${s.created} new, updated ${s.updated}, ${s.errors} errors`);
      // Mark all as imported
      const newImported = {};
      resp.data.results?.forEach(r => {
        if (r.status === "created" || r.status === "updated") {
          newImported[r.title] = r.status;
        }
      });
      setImported(prev => ({ ...prev, ...newImported }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkImporting(false);
    }
  };

  const nsLabel = { article: "Articles", template: "Templates", module: "Modules" };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded shadow-lg max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#a2a9b1] bg-[#f8f9fa] flex-shrink-0">
          <h3 className="font-bold text-[#202122]">Import from Wikipedia</h3>
          <button onClick={onClose} className="text-[#72777d] hover:text-[#202122]" data-testid="import-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#a2a9b1] flex-shrink-0">
          <button
            onClick={() => { setTab("search"); setResults([]); setCategoryMembers([]); setBulkResults(null); }}
            className={`px-4 py-2 text-sm font-medium ${tab === "search" ? "text-[#202122] border-b-2 border-[#3366cc]" : "text-[#72777d] hover:text-[#202122]"}`}
            data-testid="import-tab-search"
          >
            <Search className="w-3 h-3 inline mr-1" /> Search
          </button>
          <button
            onClick={() => { setTab("category"); setResults([]); setCategoryMembers([]); setBulkResults(null); }}
            className={`px-4 py-2 text-sm font-medium ${tab === "category" ? "text-[#202122] border-b-2 border-[#3366cc]" : "text-[#72777d] hover:text-[#202122]"}`}
            data-testid="import-tab-category"
          >
            <FolderOpen className="w-3 h-3 inline mr-1" /> Category
          </button>
        </div>

        <div className="p-4 space-y-3 flex-shrink-0">
          {/* Namespace selector */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-[#202122] font-medium">Type:</label>
            <div className="flex gap-2">
              {["article", "template", "module"].map(ns => (
                <button
                  key={ns}
                  onClick={() => setNamespace(ns)}
                  className={`px-3 py-1 text-xs rounded border ${namespace === ns ? "bg-[#3366cc] text-white border-[#3366cc]" : "border-[#a2a9b1] text-[#202122] hover:bg-[#eaf3ff]"}`}
                  data-testid={`import-ns-${ns}`}
                >
                  {nsLabel[ns]}
                </button>
              ))}
            </div>
          </div>

          {/* Search input */}
          <form onSubmit={tab === "search" ? handleSearch : handleCategorySearch} className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={tab === "search" ? "Search Wikipedia..." : "Category name (e.g. American companies)"}
              className="flex-1 px-3 py-1.5 text-sm border border-[#a2a9b1] rounded focus:outline-none focus:border-[#3366cc]"
              data-testid="import-search-input"
            />
            <button
              type="submit"
              disabled={searching || !query.trim()}
              className="px-4 py-1.5 text-sm bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d] disabled:opacity-50 flex items-center gap-1"
              data-testid="import-search-btn"
            >
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              {tab === "search" ? "Search" : "List"}
            </button>
          </form>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto border-t border-[#eaecf0]">
          {/* Search results */}
          {tab === "search" && results.length > 0 && (
            <div className="divide-y divide-[#eaecf0]">
              {results.map(r => (
                <div key={r.title} className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-[#f8f9fa]">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#3366cc]">{r.title}</div>
                    <div className="text-xs text-[#72777d] mt-0.5">{r.snippet}</div>
                    <div className="text-xs text-[#a2a9b1] mt-0.5">{r.wordcount} words</div>
                  </div>
                  {imported[r.title] ? (
                    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                      <Check className="w-3 h-3" /> {imported[r.title] === "created" ? "Imported" : "Updated"}
                    </span>
                  ) : (
                    <button
                      onClick={() => importSingle(r.title)}
                      disabled={importing[r.title]}
                      className="px-3 py-1 text-xs bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d] disabled:opacity-50 flex-shrink-0 flex items-center gap-1"
                      data-testid={`import-btn-${r.title}`}
                    >
                      {importing[r.title] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      Import
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Category results */}
          {tab === "category" && categoryMembers.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-[#f8f9fa] border-b border-[#eaecf0] flex items-center justify-between">
                <span className="text-xs text-[#54595d]">{categoryMembers.length} members found</span>
                <button
                  onClick={importAllCategory}
                  disabled={bulkImporting}
                  className="px-3 py-1 text-xs bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d] disabled:opacity-50 flex items-center gap-1"
                  data-testid="import-all-category-btn"
                >
                  {bulkImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  Import All
                </button>
              </div>
              <div className="divide-y divide-[#eaecf0]">
                {categoryMembers.map(m => (
                  <div key={m.title} className="px-4 py-2 flex items-center justify-between gap-3 hover:bg-[#f8f9fa]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3 h-3 text-[#72777d] flex-shrink-0" />
                      <span className="text-sm text-[#3366cc] truncate">{m.title}</span>
                    </div>
                    {imported[m.title] ? (
                      <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                        <Check className="w-3 h-3" /> {imported[m.title]}
                      </span>
                    ) : (
                      <button
                        onClick={() => importSingle(m.title)}
                        disabled={importing[m.title]}
                        className="px-2 py-1 text-xs bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d] disabled:opacity-50 flex-shrink-0 flex items-center gap-1"
                        data-testid={`import-btn-${m.title}`}
                      >
                        {importing[m.title] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        Import
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk import results */}
          {bulkResults && (
            <div className="p-4 border-t border-[#eaecf0]">
              <h4 className="text-sm font-bold text-[#202122] mb-2">Import Results</h4>
              <div className="flex gap-4 text-xs mb-2">
                <span className="text-green-700">Created: {bulkResults.summary.created}</span>
                <span className="text-[#3366cc]">Updated: {bulkResults.summary.updated}</span>
                {bulkResults.summary.errors > 0 && <span className="text-[#d33]">Errors: {bulkResults.summary.errors}</span>}
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {bulkResults.results?.map(r => (
                  <div key={r.title} className="flex items-center gap-2 text-xs">
                    {r.status === "created" || r.status === "updated" ? (
                      <Check className="w-3 h-3 text-green-600" />
                    ) : (
                      <XCircle className="w-3 h-3 text-[#d33]" />
                    )}
                    <span className="text-[#202122]">{r.title}</span>
                    <span className="text-[#72777d]">({r.status})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty states */}
          {tab === "search" && results.length === 0 && !searching && (
            <div className="p-8 text-center text-[#72777d] text-sm">
              Search Wikipedia to find pages to import
            </div>
          )}
          {tab === "category" && categoryMembers.length === 0 && !bulkResults && !searching && (
            <div className="p-8 text-center text-[#72777d] text-sm">
              Enter a Wikipedia category name to list its members
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WikiSidebar({ username, showTemplates = true, showUserPage = false, extraNavItems, extraToolItems }) {
  const [showRestore, setShowRestore] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const handleBackup = async () => {
    const token = localStorage.getItem("wiki_token");
    setBackingUp(true);
    try {
      const resp = await axios.get(`${API}/wiki/backup?token=${token}`, { responseType: "blob" });
      const disposition = resp.headers["content-disposition"] || "";
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `wiki_backup_${Date.now()}.json`;
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error("Backup failed");
    } finally {
      setBackingUp(false);
    }
  };

  return (
    <>
      <aside className="w-[160px] flex-shrink-0">
        <nav className="text-sm">
          <div className="mb-4">
            <h3 className="font-bold text-[#202122] mb-2">Navigation</h3>
            <ul className="space-y-1">
              <li>
                <Link to="/wiki" className="text-[#3366cc] hover:underline">Main page</Link>
              </li>
              {showTemplates && (
                <li>
                  <Link to="/wiki/templates" className="text-[#3366cc] hover:underline">Templates</Link>
                </li>
              )}
              {showUserPage && username && (
                <li>
                  <Link to={`/wiki/User:${encodeURIComponent(username)}`} className="text-[#3366cc] hover:underline">User page</Link>
                </li>
              )}
              <li>
                <Link to="/" className="text-[#3366cc] hover:underline">Marketing Site</Link>
              </li>
              {extraNavItems}
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-[#202122] mb-2">Tools</h3>
            <ul className="space-y-1">
              <li>
                <Link to="/wiki/upload" className="text-[#3366cc] hover:underline">Upload file</Link>
              </li>
              <li>
                <button
                  onClick={handleBackup}
                  disabled={backingUp}
                  className="text-[#3366cc] hover:underline flex items-center gap-1 text-left"
                  data-testid="wiki-backup-btn"
                >
                  {backingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  Backup
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowRestore(true)}
                  className="text-[#3366cc] hover:underline flex items-center gap-1 text-left"
                  data-testid="wiki-restore-btn"
                >
                  <Upload className="w-3 h-3" /> Restore
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowImport(true)}
                  className="text-[#3366cc] hover:underline flex items-center gap-1 text-left"
                  data-testid="wiki-import-btn"
                >
                  <Globe className="w-3 h-3" /> Import
                </button>
              </li>
              {extraToolItems}
            </ul>
          </div>
        </nav>
      </aside>

      {showRestore && <RestoreModal onClose={() => setShowRestore(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </>
  );
}
